import type * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Multiselect from '@cloudscape-design/components/multiselect';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Toggle from '@cloudscape-design/components/toggle';
import {
  ALL_CURRENCIES,
  ALL_PAYMENT_MODES,
  PSP_SECRET_SLOTS,
  providerAllowedInCountry,
  pspModeEnabled,
  pspSecretValueProblem,
  type Currency,
  type PaymentMode,
  type PspConfig,
  type PspSecretSlot,
  type PspSecretStatus,
} from '@etiquetachef/types';
import { subscribePspConfigs } from '@/lib/firestoreTenantDetail';
import { setPspConfigCallable } from '@/lib/callables';
import {
  adminGetPspSecretStatusCallable,
  adminSetPspSecretCallable,
  adminTestPspConnectionCallable,
  type PspConnectionResult,
} from '@/lib/pspCallables';
import { toast } from '@/lib/notifications';
import { AdminConfirm } from '@/components/ui/AdminConfirm';
import { CODE_DEFAULTS, CODE_INTEGRATED } from './pspDefaults';
import { countryDisplayName, flagEmoji } from '@/features/countries/worldCountries';
import { ALL_PLAY_LOCATIONS } from '@/features/countries/playStoreCountries';

const CURRENCY_OPTIONS = ALL_CURRENCIES.map((c) => ({ value: c, label: c }));

/**
 * `/psp/:id` — configuração de um PSP: dados do catálogo e, por modo
 * (produção e sandbox), ativo/inativo, credenciais e teste de conexão.
 *
 * Origem: #756 — 2026-10-01 13:31 +0200 (owner: "todo setup PSP deve ficar em
 * /psp e deve poder também cadastrar sandbox de cada PSP", com a chave digitada
 * na tela).
 *
 * Por quê: o seletor de PSP do país só mostra o PSP × modo ATIVO aqui
 * (`enabledPspChoices`), e as chaves passaram a ser trocadas sem deploy — o
 * callable grava uma versão nova no Secret Manager e as Functions a leem em até
 * 60 s (`apps/functions/src/lib/pspSecretStore.ts`).
 *
 * Contrato:
 *  - Salvar (motivo obrigatório) → `setPspConfig` com nome, moedas, países,
 *    observações e `modes`. PSP sem cobrança no código não liga o sandbox.
 *  - "Substituir" uma credencial → confere o prefixo do ambiente
 *    (`pspSecretValueProblem`), pede motivo e confirmação, e chama
 *    `adminSetPspSecret`, que testa a conexão ANTES de gravar. O valor nunca
 *    volta: a tela mostra só os 4 últimos caracteres, a versão e a data.
 *  - "Testar conexão" → `adminTestPspConnection` com a chave atual.
 *
 * Usado por: rota `psp/:id` (`routes/router.tsx`); `PspListPage` ("Configurar").
 *
 * Dicas: trocar o token de webhook do Asaas aqui sem trocar no Asaas faz os
 * eventos darem 401 — a tela avisa. Spec: `docs/screens/psp-admin.md`.
 * Teste: `PspEditorPage.test.tsx`.
 */
export function PspEditorPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const integrated = CODE_INTEGRATED.has(id);
  // Países do dropdown: todos, menos onde o PSP não pode cobrar (Asaas só BR).
  // #957 (2026-10-13): "todos" = os 225 locais da Google Play, o mesmo
  // universo de `/countries` — antes era a lista própria de 196 (ONU).
  const countryOptions = ALL_PLAY_LOCATIONS.filter((c) => providerAllowedInCountry(id, c))
    .map((c) => ({ value: c, label: `${flagEmoji(c)} ${countryDisplayName(c, i18n.language)}`, tags: [c] }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [exists, setExists] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [accountId, setAccountId] = useState('');
  const [modes, setModes] = useState<Record<PaymentMode, boolean>>({ live: false, sandbox: false });
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [statuses, setStatuses] = useState<PspSecretStatus[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [pending, setPending] = useState<PspSecretSlot | null>(null);
  const [busySlot, setBusySlot] = useState<string | null>(null);
  const [tests, setTests] = useState<Partial<Record<PaymentMode, PspConnectionResult>>>({});

  // Carrega o doc do catálogo uma vez (não sobrescreve o que se digita).
  useEffect(() => {
    let done = false;
    return subscribePspConfigs((r) => {
      if (done || r.kind === 'LOADING') return;
      done = true;
      // Auditoria 2026-10-01 (#756): erro de leitura NÃO é "PSP sem doc" — abrir
      // com os padrões e salvar sobrescreveria o doc real (produção desligada).
      if (r.kind === 'ERROR') {
        setLoadError(true);
        setLoaded(true);
        return;
      }
      const doc: PspConfig | undefined = r.psps.find((p) => p.id === id);
      const defaults = CODE_DEFAULTS[id];
      setExists(doc !== undefined);
      setDisplayName(doc?.display_name ?? defaults?.name ?? id);
      setCurrencies(doc?.supported_currencies ?? defaults?.currencies ?? []);
      setCountries([...(doc?.supported_countries ?? defaults?.countries ?? [])]);
      setNotes(doc?.notes ?? '');
      setAccountId(doc?.account_id ?? '');
      setModes({
        live: doc ? pspModeEnabled([doc], id, 'live') : false,
        sandbox: doc ? pspModeEnabled([doc], id, 'sandbox') : false,
      });
      setLoaded(true);
    });
  }, [id]);

  const loadStatuses = useCallback(async (): Promise<void> => {
    try {
      setStatuses(await adminGetPspSecretStatusCallable());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('admin.psp.errors.generic'));
    }
  }, [t]);

  useEffect(() => {
    if (integrated) void loadStatuses();
  }, [integrated, loadStatuses]);

  /** Chave `modo:tipo` de uma credencial (rascunhos e spinner). */
  function slotKey(s: PspSecretSlot): string {
    return `${s.mode}:${s.kind}`;
  }


  /** Valida motivo e nome e grava catálogo + modos pelo `setPspConfig`. */
  async function save(): Promise<void> {
    if (reason.trim() === '') {
      toast.error(t('admin.psp.errors.reasonRequired'));
      return;
    }
    if (displayName.trim() === '') {
      toast.error(t('admin.countries.psp.errors.nameRequired'));
      return;
    }
    setSaving(true);
    try {
      await setPspConfigCallable({
        id,
        display_name: displayName.trim(),
        enabled: modes.live,
        supported_currencies: currencies,
        supported_countries: countries,
        notes: notes.trim() === '' ? null : notes.trim(),
        account_id: accountId.trim() === '' ? null : accountId.trim(),
        modes: { live: { enabled: modes.live }, sandbox: { enabled: modes.sandbox } },
        reason: reason.trim(),
      });
      toast.success(t('admin.countries.psp.saved'));
      setExists(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('admin.psp.errors.generic'));
    } finally {
      setSaving(false);
    }
  }

  /** Confere prefixo do valor e motivo antes de pedir confirmação da troca. */
  function requestReplace(slot: PspSecretSlot): void {
    const value = drafts[slotKey(slot)] ?? '';
    const problem = pspSecretValueProblem(slot, value);
    if (problem) {
      toast.error(t(`admin.psp.valueProblem.${problem}`));
      return;
    }
    if (reason.trim() === '') {
      toast.error(t('admin.psp.errors.reasonRequired'));
      return;
    }
    setPending(slot);
  }

  /** Grava a credencial nova (`adminSetPspSecret` testa antes) e recarrega o status. */
  async function replace(slot: PspSecretSlot): Promise<void> {
    const key = slotKey(slot);
    setBusySlot(key);
    try {
      await adminSetPspSecretCallable({
        provider: slot.provider,
        mode: slot.mode,
        kind: slot.kind,
        value: drafts[key] ?? '',
        reason: reason.trim(),
      });
      toast.success(t('admin.psp.secretSaved'));
      setDrafts((d) => ({ ...d, [key]: '' }));
      await loadStatuses();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('admin.psp.errors.generic'));
    } finally {
      setBusySlot(null);
      setPending(null);
    }
  }

  /** Testa a conexão com a chave atual do modo. */
  async function test(mode: PaymentMode): Promise<void> {
    setBusySlot(`test:${mode}`);
    try {
      const r = await adminTestPspConnectionCallable({ provider: id, mode });
      setTests((x) => ({ ...x, [mode]: r }));
    } catch (err) {
      setTests((x) => ({
        ...x,
        [mode]: { ok: false, account: null, message: err instanceof Error ? err.message : String(err) },
      }));
    } finally {
      setBusySlot(null);
    }
  }

  if (!loaded) {
    return (
      <Box textAlign="center" padding="l">
        <Spinner size="large" />
      </Box>
    );
  }
  if (loadError) return <Alert type="error">{t('admin.countries.psp.errorLoad')}</Alert>;

  /** Status da credencial: ausente, carregando ou `…last4`, versão e data. */
  function statusText(slot: PspSecretSlot): string {
    if (statuses === null) return t('admin.psp.secretLoading');
    const st = statuses.find((s) => s.provider === slot.provider && s.mode === slot.mode && s.kind === slot.kind);
    if (!st?.configured) return t('admin.psp.secretMissing');
    const when = st.createdAt ? new Date(st.createdAt).toLocaleString() : '—';
    return t('admin.psp.secretConfigured', { last4: st.last4 ?? '', version: st.version ?? '—', when });
  }

  /** Bloco de um modo: toggle, credenciais e teste de conexão. */
  function modeBlock(mode: PaymentMode): React.JSX.Element {
    const slots = PSP_SECRET_SLOTS.filter((s) => s.provider === id && s.mode === mode);
    const testable = slots.some((s) => s.testable);
    const result = tests[mode];
    return (
      <Container
        key={mode}
        header={<Header variant="h2" description={t(`admin.psp.modeDescription.${mode}`)}>{t(`admin.psp.mode.${mode}`)}</Header>}
      >
        <SpaceBetween size="m">
          <Toggle
            checked={modes[mode]}
            disabled={mode === 'sandbox' && !integrated}
            onChange={({ detail }): void => {
              setModes((m) => ({ ...m, [mode]: detail.checked }));
            }}
          >
            {modes[mode] ? t('admin.countries.psp.enabled') : t('admin.countries.psp.disabled')}
          </Toggle>
          {slots.map((slot) => {
            const key = slotKey(slot);
            return (
              <FormField
                key={key}
                label={t(`admin.psp.kind.${slot.kind}`)}
                description={statusText(slot)}
                secondaryControl={
                  <Button loading={busySlot === key} onClick={(): void => {
                      requestReplace(slot);
                    }}>
                    {t('admin.psp.replace')}
                  </Button>
                }
              >
                <Input
                  type="password"
                  // Sem isto o navegador oferece a senha do admin num token
                  // sem prefixo (webhook/saque) — auditoria #756.
                  autoComplete="new-password"
                  value={drafts[key] ?? ''}
                  placeholder={slot.prefixes.length > 0 ? `${slot.prefixes[0] ?? ''}…` : t('admin.psp.tokenPlaceholder')}
                  onChange={({ detail }): void => {
                    setDrafts((d) => ({ ...d, [key]: detail.value }));
                  }}
                />
              </FormField>
            );
          })}
          {slots.some((s) => s.provider === 'asaas' && s.kind === 'webhook') && (
            <Alert type="warning">{t('admin.psp.webhookWarning')}</Alert>
          )}
          {testable && (
            <SpaceBetween size="xs">
              <Button loading={busySlot === `test:${mode}`} onClick={(): void => void test(mode)}>
                {t('admin.psp.test')}
              </Button>
              {result && (
                <Alert type={result.ok ? 'success' : 'error'}>
                  {result.ok
                    ? t('admin.psp.testOk', { account: result.account ?? '' })
                    : t('admin.psp.testFailed', { message: result.message ?? '' })}
                </Alert>
              )}
            </SpaceBetween>
          )}
        </SpaceBetween>
      </Container>
    );
  }

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={exists ? t('admin.psp.editorSubtitle') : t('admin.psp.notInCatalogHint')}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                iconName="arrow-left"
                onClick={(): void => {
                  void navigate('/psp');
                }}
              >
                {t('admin.psp.back')}
              </Button>
              {/* Salvar também no topo (owner, 2026-10-01). */}
              <Button variant="primary" loading={saving} onClick={(): void => void save()}>
                {t('admin.countries.psp.save')}
              </Button>
            </SpaceBetween>
          }
        >
          {displayName || id}
        </Header>
      }
    >
      <Form
        actions={
          <Button variant="primary" loading={saving} onClick={(): void => void save()}>
            {t('admin.countries.psp.save')}
          </Button>
        }
      >
        <SpaceBetween size="l">
          <Container header={<Header variant="h2">{t('admin.psp.general')}</Header>}>
            <ColumnLayout columns={2}>
              <FormField label={t('admin.countries.psp.nameLabel')}>
                <Input value={displayName} onChange={({ detail }): void => {
                    setDisplayName(detail.value);
                  }} />
              </FormField>
              <FormField label={t('admin.countries.psp.currenciesLabel')}>
                <Multiselect
                  selectedOptions={CURRENCY_OPTIONS.filter((o) => currencies.includes(o.value))}
                  options={CURRENCY_OPTIONS}
                  placeholder={t('admin.countries.psp.currenciesPlaceholder')}
                  onChange={({ detail }): void => {
                    setCurrencies(detail.selectedOptions.map((o) => o.value as Currency));
                  }}
                />
              </FormField>
              <FormField label={t('admin.countries.psp.countriesLabel')} description={t('admin.psp.countriesHint')}>
                {/* Dropdown (owner, 2026-10-01): só os países em que o PSP pode
                    cobrar (`providerAllowedInCountry` — o Asaas só no BR). */}
                <Multiselect
                  selectedOptions={countryOptions.filter((o) => countries.includes(o.value))}
                  options={countryOptions}
                  filteringType="auto"
                  placeholder={t('admin.psp.countriesPlaceholder')}
                  onChange={({ detail }): void => {
                    setCountries(detail.selectedOptions.flatMap((o) => (o.value ? [o.value] : [])));
                  }}
                />
              </FormField>
              {/* Dado da CONTA do PSP (owner, 2026-10-01): o `acct_…` do Stripe
                  fica aqui, não na entidade. A troca da chave confere contra ele. */}
              {id === 'stripe' && (
                <FormField label={t('admin.psp.accountId')} description={t('admin.psp.accountIdHint')}>
                  <Input
                    value={accountId}
                    placeholder="acct_…"
                    onChange={({ detail }): void => {
                      setAccountId(detail.value);
                    }}
                  />
                </FormField>
              )}
              <FormField label={t('admin.countries.psp.notesLabel')}>
                <Input value={notes} onChange={({ detail }): void => {
                    setNotes(detail.value);
                  }} />
              </FormField>
            </ColumnLayout>
          </Container>

          {!integrated && <Alert type="info">{t('admin.psp.catalogOnlyHint')}</Alert>}
          {ALL_PAYMENT_MODES.map((mode) => modeBlock(mode))}

          <Container>
            <FormField label={t('admin.psp.reason')} description={t('admin.psp.reasonHint')}>
              <Input value={reason} onChange={({ detail }): void => {
                    setReason(detail.value);
                  }} />
            </FormField>
          </Container>
        </SpaceBetween>
      </Form>

      <AdminConfirm
        visible={pending !== null}
        title={t('admin.psp.replaceTitle')}
        confirmLabel={t('admin.psp.replace')}
        cancelLabel={t('admin.countries.psp.cancel')}
        loading={busySlot !== null}
        onConfirm={(): void => {
          if (pending) void replace(pending);
        }}
        onDismiss={(): void => {
          setPending(null);
        }}
      >
        <Box variant="p">
          {pending
            ? t('admin.psp.replaceDescription', {
                kind: t(`admin.psp.kind.${pending.kind}`),
                mode: t(`admin.psp.mode.${pending.mode}`),
              })
            : ''}
        </Box>
      </AdminConfirm>
    </ContentLayout>
  );
}
